import { useMemo, useState } from 'react';

import {
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { OnboardingFlow } from '../../../../../backend/src/user/onboarding-flow';
import { useDeleteFiles } from '@/api/mutations/useDeleteFiles';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import type { File } from '@/api/types/backend-dto-types';
import { CoachMark } from '@/new/components/coach-mark';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { formatFileSize } from '@/new/components/file-uploader/utils';
import { FilterMultiSelect } from '@/new/components/filter/filter-multi-select';
import { FilterPopoverContent } from '@/new/components/filter-popover-content';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { ScrollableTable } from '@/new/components/table/scrollable-table';
import { TableNullField } from '@/new/components/table/table-null-field';
import { formatDatetimeForTable, includedInArr, selectColumn } from '@/new/lib/table-utils';
import { cn } from '@/new/lib/utils';

const fallbackTableData: File[] = [];
const columnHelper = createColumnHelper<File>();

export function FilesTable({
  files,
  isLoading,
  onViewUploadDetails,
  uploadResultCoachMarkUploadId,
  onPreviewDoc,
}: {
  files: File[];
  isLoading: boolean;
  onViewUploadDetails?: (uploadId: string) => void;
  uploadResultCoachMarkUploadId?: string;
  onPreviewDoc?: (docId: string) => void;
}) {
  const { matterId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingFileIds, setDeletingFileIds] = useState<Set<string>>(new Set());
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);

  if (!matterId) return null;

  // Get unique file extensions from the files
  const fileExtensions = useMemo(() => {
    const extensions = files.map((file) => file.extension).filter((ext): ext is string => !!ext);
    return Array.from(new Set(extensions)).sort();
  }, [files]);

  // Build a map of uploadId → formatted date label for filtering
  const uploadOptions = useMemo(() => {
    const uploads = new Map<string, string>();
    for (const file of files) {
      if (file.uploadId && file.uploadDate) {
        if (!uploads.has(file.uploadId)) {
          uploads.set(
            file.uploadId,
            new Date(file.uploadDate).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }),
          );
        }
      }
    }
    // Sort by date descending (newest first)
    return Array.from(uploads.entries())
      .sort((a, b) => b[1].localeCompare(a[1]))
      .map(([id, label]) => ({ id, label }));
  }, [files]);

  const clearAllFilters = () => {
    setSearchQuery('');
    setColumnFilters([]);
  };

  // Fetch matter to get all docs caseId - to be removed when files module is re-worked to use matterId
  const { data: matter } = useGetMatterV2(matterId);
  const { mutate: deleteFiles, isLoading: isDeletingFiles } = useDeleteFiles({
    caseId: matter?.allDocumentsCaseId ?? '',
    matterId,
  });

  const columns = useMemo(
    () => [
      selectColumn<File>(),
      columnHelper.accessor('name', {
        header: () => <span>Filename</span>,
        cell: (info) => (
          <Tooltip delayDuration={500}>
            <TooltipTrigger
              className={cn('flex items-center', onPreviewDoc && 'cursor-pointer hover:text-gray-950 hover:underline')}
              onClick={onPreviewDoc ? () => onPreviewDoc(info.row.original.docId) : undefined}
            >
              <span className="line-clamp-1 text-left break-all">{info.getValue()}</span>
            </TooltipTrigger>
            <TooltipContent>{info.getValue()}</TooltipContent>
          </Tooltip>
        ),
        meta: {
          sortIcon: 'string',
        },
      }),
      columnHelper.accessor('size', {
        header: () => <span>File Size</span>,
        cell: (info) => formatFileSize(info.getValue()),
        meta: {
          sortIcon: 'number',
        },
      }),
      columnHelper.accessor('numberOfPages', {
        header: () => <span>Pages</span>,
        cell: (info) => {
          const pageCount = info.getValue();
          return pageCount === null ? <TableNullField /> : pageCount.toLocaleString();
        },
        meta: {
          sortIcon: 'number',
        },
      }),
      columnHelper.accessor('uploadId', {
        header: () => null,
        cell: () => null,
        enableHiding: true,
      }),
      columnHelper.accessor('extension', {
        header: () => <span>Extension</span>,
        cell: (info) => (
          <span className="rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600 uppercase">
            {info.getValue()}
          </span>
        ),
        filterFn: includedInArr,
        meta: {
          sortIcon: 'string',
        },
      }),
      columnHelper.accessor('createdDate', {
        header: () => <span>Uploaded</span>,
        cell: (info) => <span className="whitespace-nowrap">{formatDatetimeForTable(info.getValue())}</span>,
        meta: {
          sortIcon: 'number',
        },
      }),
      columnHelper.display({
        id: 'delete',
        header: () => <span></span>,
        cell: (info) => (
          <div className="flex items-center">
            <Button
              size="iconSm"
              variant="ghost"
              className="p-0 hover:text-red-500"
              onClick={() => setPendingDeleteIds([info.row.original.id])}
            >
              <Icon className="lucide--x" />
            </Button>
          </div>
        ),
      }),
    ],
    [onPreviewDoc],
  );

  const table = useReactTable({
    data: files ?? fallbackTableData,
    columns,
    state: {
      rowSelection,
      globalFilter: searchQuery,
      columnFilters,
      columnVisibility: { uploadId: false },
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setSearchQuery,
    onColumnFiltersChange: setColumnFilters,
    initialState: {
      pagination: {
        pageSize: 50,
      },
      sorting: [{ id: 'createdDate', desc: true }],
    },
    meta: {
      getRowClassName: (row: File) => {
        return deletingFileIds.has(row.id) ? 'opacity-50' : '';
      },
      getGroupKey: (row: File) => row.uploadId ?? '',
      formatGroupLabel: (row: File) => {
        if (!row.uploadDate) return 'Unknown batch';
        return `Uploaded on ${new Date(row.uploadDate).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'medium' })}`;
      },
      renderGroupActions: (row: File) => {
        const { uploadId } = row;
        if (!uploadId || !onViewUploadDetails) return null;

        const viewDetailsButton = (
          <Button
            size="sm"
            variant="ghost"
            className="px-2 text-xs font-medium"
            onClick={() => onViewUploadDetails(uploadId)}
          >
            <span>View details</span>
            <Icon className="lucide--list-collapse" />
          </Button>
        );

        if (uploadResultCoachMarkUploadId !== uploadId) {
          return viewDetailsButton;
        }

        return (
          <CoachMark
            flowId={OnboardingFlow.UploadResultCoachMark}
            side="left"
            align="center"
            sideOffset={0}
            delayMs={0}
            content="You can view the details of your recent upload here"
            className="w-80"
          >
            {viewDetailsButton}
          </CoachMark>
        );
      },
    },
  });

  // Get selected file IDs
  const selectedFileIds = Object.keys(rowSelection);

  const handleRemoveSelectedFiles = () => {
    if (selectedFileIds.length === 0) return;
    setPendingDeleteIds(selectedFileIds);
  };

  const confirmDelete = () => {
    const ids = pendingDeleteIds;
    ids.forEach((fileId) => {
      setDeletingFileIds((prev) => new Set(prev).add(fileId));
    });

    deleteFiles(ids, {
      onSettled: () => {
        setDeletingFileIds((prev) => {
          const next = new Set(prev);
          ids.forEach((fileId) => next.delete(fileId));
          return next;
        });
        setRowSelection({});
        setPendingDeleteIds([]);
      },
    });
  };

  const extensionFilterValue = table.getColumn('extension')?.getFilterValue();
  const selectedExtensions = Array.isArray(extensionFilterValue) ? extensionFilterValue : [];
  const uploadFilterValue = table.getColumn('uploadId')?.getFilterValue();
  const selectedUploads = Array.isArray(uploadFilterValue) ? uploadFilterValue : [];

  return (
    <>
      <ScrollableTable
        className="-mx-1 grow px-1 text-gray-700"
        table={table}
        plural="uploaded files"
        globalFilter={searchQuery}
        setGlobalFilter={setSearchQuery}
        loading={isLoading}
        filters={{
          popover: (
            <FilterPopoverContent clearAllFilters={clearAllFilters}>
              <div>
                <p className="mb-3 text-gray-600">File Extension</p>
                <FilterMultiSelect
                  values={selectedExtensions}
                  options={fileExtensions}
                  column={table.getColumn('extension')!}
                  placeholder="Select file extensions..."
                  formatLabel={(ext) => ext.toUpperCase()}
                />
              </div>
              {uploadOptions.length > 1 && (
                <div>
                  <p className="mb-3 text-gray-600">Upload Batch</p>
                  <FilterMultiSelect
                    values={selectedUploads}
                    options={uploadOptions.map((u) => u.id)}
                    column={table.getColumn('uploadId')!}
                    placeholder="Select upload batches..."
                    formatLabel={(id) => uploadOptions.find((u) => u.id === id)?.label ?? id}
                  />
                </div>
              )}
            </FilterPopoverContent>
          ),
          count: columnFilters.length,
          clearAll: clearAllFilters,
        }}
        additionalButtons={
          <Button disabled={selectedFileIds.length === 0 || isDeletingFiles} onClick={handleRemoveSelectedFiles}>
            <Icon className="lucide--trash" />
            <span>Remove Selected ({selectedFileIds.length})</span>
          </Button>
        }
      />

      <ConfirmationModal
        isOpen={pendingDeleteIds.length > 0}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteIds([]);
        }}
        title="Remove Files"
        onConfirm={confirmDelete}
        isLoading={isDeletingFiles}
      >
        <p>
          Are you sure you want to remove{' '}
          {pendingDeleteIds.length === 1 ? 'this file' : `${pendingDeleteIds.length} files`}? This action cannot be
          undone.
        </p>
      </ConfirmationModal>
    </>
  );
}
